import { ArrowLeftIcon, TrashIcon, PlusIcon } from '@heroicons/react/20/solid'
import { PhotoIcon, ArrowRightIcon } from '@heroicons/react/20/solid'
import { useNavigate, useParams } from 'react-router-dom'
import * as Yup from 'yup'
import { useFormik } from 'formik'

import { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { ErrorDialog, SuccessDialog } from 'components/custom/ResponseDialog'
import { useLazyQuery, useMutation } from '@apollo/client'
import { ADD_CAPTAIN_SHIFT_SCHEDULE, UPDATE_CAPTAIN_SHIFT_SCHEDULE } from 'graphql/route/mutations'
import { GET_CAPTAIN_SHIFT_SCHEDULE_LIST } from 'graphql/route/queries'
import { UserListDropdown } from 'components/custom/userListDropdown'
import { DAYS_OF_WEEK } from 'pages/constants'
import { CommonStatusDropdown } from 'components/custom/commonStatusDropdown'
import Flatpickr from 'react-flatpickr'
import 'flatpickr/dist/themes/material_blue.css'

export default function CaptainScheduleForm() {
    const { id, route_id, trip_id, trip_name } = useParams()
    const navigate = useNavigate()
    const { t } = useTranslation()
    const [showErrDialog, setShowErrDialog] = useState(false)
    const [showSucDialog, setShowSucDialog] = useState(false)
    const [addError, setAddError] = useState(t('Problem Adding Boat'))
    const [submitLoading, setSubmitLoading] = useState(false)
    const [shiftSchedule, setShiftSchedule] = useState(null)
    const [initialWeekDays, setInitialWeekDays] = useState([])

    useEffect(() => {
        if (id) {
            console.log('ID:', id)
            if (id != 0) getCaptainShiftScheduleById()
        }
    }, [id])

    const handleBack = () => {
        navigate(`/metro-routes/captain-details/${route_id}/${trip_id}`)
    }
    const handleDialogClose = () => {
        setShowSucDialog(false)
        setShowErrDialog(false)
    }

    // Mutation Add Captain Shift Schedule
    const [addCaptainShiftSchedule] = useMutation(ADD_CAPTAIN_SHIFT_SCHEDULE, {
        onCompleted: data => {
            if (data.createCaptainShiftSchedule.code === '200' && data.createCaptainShiftSchedule.success) {
                setSubmitLoading(false)
                setShowSucDialog(true)
                setTimeout(() => {
                    handleBack()
                }, 500)
            } else {
                setSubmitLoading(false)
                setAddError(data.createCaptainShiftSchedule.message)
                setShowErrDialog(true)
            }
        },
        onError: error => {
            setSubmitLoading(false)
            setAddError(error.message)
            setShowErrDialog(true)
        },
    })

    //Mutation Update Captain Shift Schedule
    const [updateCaptainShiftSchedule] = useMutation(UPDATE_CAPTAIN_SHIFT_SCHEDULE, {
        onCompleted: data => {
            if (data.updateCaptainShiftSchedule.success) {
                setSubmitLoading(false)
                setShowSucDialog(true)
                setTimeout(() => {
                    handleBack()
                }, 1000)
            } else {
                setAddError(data.updateCaptainShiftSchedule.message)
                setShowErrDialog(true)
            }
        },
        onError: error => {
            setShowSucDialog(false)
            setAddError(error.message)
            setShowErrDialog(true)
        },
    })

    // Submit Shift Schedule
    const submitShiftSchedule = values => {
        if (id != 0) {
            updateCaptainShiftSchedule({
                variables: {
                    shiftScheduleId: id,
                    captainShiftSchedule: {
                        captain_id: values.captainId,
                        route_id: route_id,
                        trip_id: trip_id,
                        days_of_week: values.daysOfWeek,
                        start_time: values.startTime.slice(0, 5), // Format to HH:mm
                        end_time: values.endTime.slice(0, 5), // Format to HH:mm
                        valid_from: values.validFrom,
                        valid_to: values.validTo,
                        status_code: values.status,
                    },
                },
            })
        } else {
            addCaptainShiftSchedule({
                variables: {
                    captainShiftSchedule: {
                        captain_id: values.captainId,
                        route_id: route_id,
                        trip_id: trip_id,
                        days_of_week: values.daysOfWeek,
                        start_time: values.startTime,
                        end_time: values.endTime,
                        valid_from: values.validFrom,
                        valid_to: values.validTo,
                        status_code: values.status || 'ACT',
                    },
                },
            })
        }
    }
    // For Edit Schedule
    const [getCaptainShiftSchedule, { loading: scheduleListLoading, error: scheduleListError }] = useLazyQuery(
        GET_CAPTAIN_SHIFT_SCHEDULE_LIST,
        {
            fetchPolicy: 'network-only',
            notifyOnNetworkStatusChange: true,
            onCompleted: data => {
                const shiftSchedules = data.getCaptainShiftSchedule.shift_schedules[0]
                setShiftSchedule(shiftSchedules)
                setInitialWeekDays(shiftSchedules.days_of_week)
            },
        }
    )
    const getCaptainShiftScheduleById = () => {
        getCaptainShiftSchedule({
            variables: {
                page: {
                    limit: 1,
                    offset: 0,
                },
                shiftScheduleFilter: {
                    shift_schedule_id: id,
                    captain_id: null,
                    route_id: route_id,
                    valid_from: null,
                    valid_to: null,
                    status_code: null,
                },
            },
        })
    }
    // Form Validation
    const shiftFormValidation = useFormik({
        enableReinitialize: true,
        initialValues: {
            captainId: shiftSchedule?.captain?.id || '',
            daysOfWeek: initialWeekDays || [],
            startTime: shiftSchedule?.start_time || '',
            endTime: shiftSchedule?.end_time || '',
            validFrom: shiftSchedule?.valid_from || '',
            validTo: shiftSchedule?.valid_to || '',
            status: shiftSchedule?.status?.code || 'ACT',
        },
        validationSchema: Yup.object({
            captainId: Yup.string().required('Captain is required'),
            daysOfWeek: Yup.array().min(1, 'At least one day must be selected').required('Days of week is required'),
            startTime: Yup.string().required('Start time is required'),
            endTime: Yup.string().required('End time is required'),
            validFrom: Yup.string().required('From date is required'),
            // validTo: Yup.string().required('Till date is required'),
            // status: Yup.string().required('Status is required')
        }),
        onSubmit: values => {
            console.log('Form Data:', values)
            setSubmitLoading(true)
            submitShiftSchedule(values)
        },
    })

    // Handling checkbox change for days of the week
    const handleWeekDaysCheckboxChange = e => {
        const { value } = e.target
        if (shiftFormValidation.values.daysOfWeek.includes(value)) {
            const newDays = shiftFormValidation.values.daysOfWeek.filter(day => day !== value)
            shiftFormValidation.setFieldValue('daysOfWeek', newDays)
        } else {
            const newDays = [...shiftFormValidation.values.daysOfWeek, value]
            shiftFormValidation.setFieldValue('daysOfWeek', newDays)
        }
    }

    return (
        <>
            <div className="mb-8 flex justify-between items-center">
                <div>
                    <button
                        onClick={handleBack}
                        type="button"
                        className="inline-flex items-center gap-x-0.5 rounded-md text-sm"
                    >
                        <ArrowLeftIcon aria-hidden="true" className="-ml-0.5 h-5 w-5 rtl:rotate-180" />
                        {t('Back')}
                    </button>
                </div>
                <div className="flex gap-3">
                    <button
                        onClick={handleBack}
                        type="button"
                        className="inline-flex items-center justify-center rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50"
                    >
                        {t('Cancel')}
                    </button>
                    <button
                        disabled={submitLoading}
                        onClick={() => {
                            shiftFormValidation.handleSubmit()
                        }}
                        type="button"
                        className="hover:bg-primary/[.9] bg-primary focus-visible:outline-primary justify-center rounded-md px-3 py-1.5 text-sm font-semibold leading-6 text-white shadow-sm focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2"
                    >
                        {t('Save')}
                    </button>
                </div>
            </div>
            <div className="md:flex md:items-center md:space-x-5 mb-8 border-b-[1px] border-solid border-gray-200">
                {/* <div className="flex items-start space-x-5">
                    <div className="flex-shrink-0 mb-4">
                        <div className="relative">
                            <div className="w-12 h-12 bg-gray-50 p-4 flex justify-center items-center">
                                <svg
                                    height="100%"
                                    viewBox="0 0 512 512"
                                    width="100%"
                                    className="fill-primary"
                                    xmlns="http://www.w3.org/2000/svg"
                                    id="fi_3710370"
                                >
                                    <g id="Navigator">
                                        <path d="m456.59 74.3682a86.939 86.939 0 0 0 -132.2211 112.0053l56.8331 79.5625a17.1 17.1 0 0 0 27.832 0l56.833-79.5625a86.6665 86.6665 0 0 0 -9.277-112.0053zm-61.472 89.9763a28.5 28.5 0 1 1 28.5-28.5 28.5014 28.5014 0 0 1 -28.5 28.5z"></path>
                                        <path d="m395.118 314.9019a17.0965 17.0965 0 0 0 -17.098 17.0981v36.46a60.4419 60.4419 0 1 1 -120.8838 0v-155.08a94.6382 94.6382 0 1 0 -189.2763 0v80.62a17.0982 17.0982 0 0 0 34.1963 0v-80.62a60.4419 60.4419 0 1 1 120.8837 0v155.08a94.6382 94.6382 0 0 0 189.2764 0v-36.46a17.0965 17.0965 0 0 0 -17.0983-17.0981z"></path>
                                        <path d="m84.9578 352.9019a55.0981 55.0981 0 1 0 55.0982 55.0981 55.1578 55.1578 0 0 0 -55.0982-55.0981z"></path>
                                    </g>
                                </svg>
                            </div>
                            <span aria-hidden="true" className="absolute inset-0 rounded-lg shadow-inner" />
                        </div>
                    </div>
                    <div className="pt-1">
                        <p className="text-sm font-medium text-gray-500">{t('Route Name')}</p>
                        <h1 className="text-md font-bold text-gray-900">sample Route 1</h1>
                    </div>
                </div> */}
                {/* <span className="inline-flex items-center px-3 py-1.5 text-sm font-medium rounded-full bg-white text-primary">
                    <ArrowRightIcon aria-hidden="true" className="-ml-0.5 h-5 w-5" />
                </span> */}
                <div className="flex items-start space-x-5">
                    <div className="flex-shrink-0 mb-4">
                        <div className="relative">
                            <div className="w-12 h-12 bg-gray-50 p-4 flex justify-center items-center">
                                <svg
                                    height="100%"
                                    viewBox="0 0 512 512"
                                    width="100%"
                                    className="fill-primary"
                                    xmlns="http://www.w3.org/2000/svg"
                                    id="fi_3710370"
                                >
                                    <g id="Navigator">
                                        <path d="m456.59 74.3682a86.939 86.939 0 0 0 -132.2211 112.0053l56.8331 79.5625a17.1 17.1 0 0 0 27.832 0l56.833-79.5625a86.6665 86.6665 0 0 0 -9.277-112.0053zm-61.472 89.9763a28.5 28.5 0 1 1 28.5-28.5 28.5014 28.5014 0 0 1 -28.5 28.5z"></path>
                                        <path d="m395.118 314.9019a17.0965 17.0965 0 0 0 -17.098 17.0981v36.46a60.4419 60.4419 0 1 1 -120.8838 0v-155.08a94.6382 94.6382 0 1 0 -189.2763 0v80.62a17.0982 17.0982 0 0 0 34.1963 0v-80.62a60.4419 60.4419 0 1 1 120.8837 0v155.08a94.6382 94.6382 0 0 0 189.2764 0v-36.46a17.0965 17.0965 0 0 0 -17.0983-17.0981z"></path>
                                        <path d="m84.9578 352.9019a55.0981 55.0981 0 1 0 55.0982 55.0981 55.1578 55.1578 0 0 0 -55.0982-55.0981z"></path>
                                    </g>
                                </svg>
                            </div>
                            <span aria-hidden="true" className="absolute inset-0 rounded-lg shadow-inner" />
                        </div>
                    </div>
                    <div className="pt-1">
                        <p className="text-sm font-medium text-gray-500">{t('Trip Name')}</p>
                        <h1 className="text-md font-bold text-gray-900">{trip_name}</h1>
                    </div>
                </div>
            </div>
            <form>
                <div className="space-y-12">
                    <div className="grid grid-cols-1 gap-x-8 gap-y-10 border-b border-gray-900/10 pb-12 md:grid-cols-3">
                        <div>
                            <h2 className="text-base font-semibold leading-7 text-gray-900">{t('Captain Shift')}</h2>
                            <p className="mt-1 text-sm leading-6 text-gray-600">
                                {id != 0 ? 'Edit' : 'Add'} {t('Captain Shift Schedule')}
                            </p>
                        </div>
                        <div className="grid max-w-2xl grid-cols-1 gap-x-6 gap-y-4 sm:grid-cols-6 md:col-span-2">
                            <div className="sm:col-span-full">
                                <label htmlFor="captain" className="block text-sm font-medium leading-6 text-gray-900">
                                    {t('Captain')}
                                    <span className="text-red">*</span>
                                </label>
                                <div className="mt-2">
                                    <UserListDropdown
                                        userType="captain"
                                        fields={shiftFormValidation.getFieldProps('captainId')}
                                        purpose="select"
                                        isActive={true}
                                    />
                                    {shiftFormValidation.touched.captainId && shiftFormValidation.errors.captainId && (
                                        <p className="text-error text-xs mt-1">
                                            {shiftFormValidation.errors.captainId}
                                        </p>
                                    )}
                                </div>
                            </div>

                            <div className="sm:col-span-3">
                                <label htmlFor="status" className="block text-sm font-medium leading-6 text-gray-900">
                                    {t('Status')}
                                    <span className="text-red">*</span>
                                </label>
                                <div className="mt-2">
                                    <CommonStatusDropdown
                                        statusFor="CaptainShiftScheduleStatus"
                                        fields={shiftFormValidation.getFieldProps('status')}
                                        purpose="add"
                                    />
                                </div>
                            </div>

                            <fieldset className="sm:col-span-6">
                                <legend className="block text-sm font-medium leading-6 text-gray-900">
                                    Days <span className="text-red">*</span>
                                </legend>

                                <div className="grid max-w-2xl grid-cols-1 gap-x-6 gap-y-3 sm:grid-cols-6 md:col-span-2">
                                    {DAYS_OF_WEEK.map(day => (
                                        <div key={day.code} className="sm:col-span-2">
                                            <div className="mt-2 space-y-6 block w-full rounded-md border border-gray-300 py-1.5 px-2 text-gray-900 shadow-sm sm:max-w-xs sm:text-sm sm:leading-6">
                                                <div className="relative flex gap-x-3">
                                                    <div className="flex h-6 items-center">
                                                        <input
                                                            id={day.code}
                                                            name="daysOfWeek"
                                                            type="checkbox"
                                                            value={day.code}
                                                            checked={shiftFormValidation.values.daysOfWeek.includes(
                                                                day.code
                                                            )}
                                                            onChange={handleWeekDaysCheckboxChange}
                                                            className="rounded border-gray-300 text-primary focus:ring-primary"
                                                        />
                                                    </div>
                                                    <label htmlFor={day.code} className="text-gray-900 w-full">
                                                        {day.label}
                                                    </label>
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                                {shiftFormValidation.touched.daysOfWeek && shiftFormValidation.errors.daysOfWeek && (
                                    <p className="text-error text-xs mt-1">{shiftFormValidation.errors.daysOfWeek}</p>
                                )}
                            </fieldset>

                            <div className="sm:col-span-3">
                                <label
                                    htmlFor="startTime"
                                    className="block text-sm font-medium leading-6 text-gray-900"
                                >
                                    {t('Start Time')}
                                    <span className="text-red">*</span>
                                </label>
                                <div className="mt-2">
                                    <input
                                        id="startTime"
                                        name="startTime"
                                        type="time"
                                        value={shiftFormValidation.values.startTime.slice(0, 5)}
                                        onChange={shiftFormValidation.handleChange}
                                        onBlur={shiftFormValidation.handleBlur}
                                        className="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 focus:ring-2 focus:ring-inset focus:ring-primary sm:max-w-xs sm:text-sm sm:leading-6"
                                    />
                                    {shiftFormValidation.touched.startTime && shiftFormValidation.errors.startTime && (
                                        <p className="text-error text-xs mt-1">
                                            {shiftFormValidation.errors.startTime}
                                        </p>
                                    )}
                                </div>
                            </div>

                            <div className="sm:col-span-3">
                                <label htmlFor="endTime" className="block text-sm font-medium leading-6 text-gray-900">
                                    {t('End Time')}
                                    <span className="text-red">*</span>
                                </label>
                                <div className="mt-2">
                                    <input
                                        id="endTime"
                                        name="endTime"
                                        type="time"
                                        value={shiftFormValidation.values.endTime.slice(0, 5)}
                                        onChange={shiftFormValidation.handleChange}
                                        onBlur={shiftFormValidation.handleBlur}
                                        className="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 focus:ring-2 focus:ring-inset focus:ring-primary sm:max-w-xs sm:text-sm sm:leading-6"
                                    />
                                    {shiftFormValidation.touched.endTime && shiftFormValidation.errors.endTime && (
                                        <p className="text-error text-xs mt-1">{shiftFormValidation.errors.endTime}</p>
                                    )}
                                </div>
                            </div>

                            <div className="sm:col-span-3">
                                <label
                                    htmlFor="from-date"
                                    className="block text-sm font-medium leading-6 text-gray-900"
                                >
                                    {t('Valid From')}
                                    <span className="text-red">*</span>
                                </label>
                                <div className="mt-2">
                                    <Flatpickr
                                        id="validFrom"
                                        name="validFrom"
                                        placeholder="yyyy/mm/dd"
                                        value={shiftFormValidation.values.validFrom}
                                        onChange={date =>
                                            shiftFormValidation.setFieldValue(
                                                'validFrom',
                                                date[0] ? date[0].toLocaleDateString('en-CA').replace(/\//g, '-') : ''
                                            )
                                        }
                                        options={{
                                            dateFormat: 'Y/m/d', // Date format to display as 'YYYY/MM/DD'
                                            maxDate: '2050-12-31',
                                            altInput: true,
                                            altFormat: 'Y/m/d',
                                        }}
                                        className="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 focus:ring-2 focus:ring-inset focus:ring-primary sm:max-w-xs sm:text-sm sm:leading-6"
                                    />

                                    {shiftFormValidation.touched.validFrom && shiftFormValidation.errors.validFrom && (
                                        <p className="text-error text-xs mt-1">
                                            {shiftFormValidation.errors.validFrom}
                                        </p>
                                    )}
                                    {/* <input
                                        id="validFrom"
                                        name="validFrom"
                                        type="date"
                                        {...shiftFormValidation.getFieldProps('validFrom')}
                                        className="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 focus:ring-2 focus:ring-inset focus:ring-primary sm:max-w-xs sm:text-sm sm:leading-6"
                                        max="2050-12-31"
                                    />
                                    {
                                        shiftFormValidation.touched.validFrom && shiftFormValidation.errors.validFrom &&
                                        <p className="text-error text-xs mt-1">{shiftFormValidation.errors.validFrom}</p>
                                    } */}
                                </div>
                            </div>

                            <div className="sm:col-span-3">
                                <label
                                    htmlFor="till-date"
                                    className="block text-sm font-medium leading-6 text-gray-900"
                                >
                                    {t('Valid To')}
                                    {/* <span className="text-red">*</span> */}
                                </label>
                                <div className="mt-2">
                                    {/* <input
                                        id="validTo"
                                        name="validTo"
                                        type="date"
                                        {...shiftFormValidation.getFieldProps('validTo')}
                                        className="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 focus:ring-2 focus:ring-inset focus:ring-primary sm:max-w-xs sm:text-sm sm:leading-6"
                                        max="2050-12-31"
                                        min={shiftFormValidation.values.validFrom}
                                    />
                                    {
                                        shiftFormValidation.touched.validTo && shiftFormValidation.errors.validTo &&
                                        <p className="text-error text-xs mt-1">{shiftFormValidation.errors.validTo}</p>
                                    } */}
                                    <Flatpickr
                                        id="validTo"
                                        name="validTo"
                                        placeholder="yyyy/mm/dd"
                                        value={shiftFormValidation.values.validTo}
                                        onChange={date =>
                                            shiftFormValidation.setFieldValue(
                                                'validTo',
                                                date[0] ? date[0].toLocaleDateString('en-CA').replace(/\//g, '-') : ''
                                            )
                                        }
                                        options={{
                                            dateFormat: 'Y/m/d', // Display date format as 'YYYY/MM/DD'
                                            maxDate: '2050-12-31', // Set the maximum date
                                            minDate: shiftFormValidation.values.validFrom, // Set the minimum date dynamically
                                            altInput: true,
                                            altFormat: 'Y/m/d',
                                        }}
                                        className="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 focus:ring-2 focus:ring-inset focus:ring-primary sm:max-w-xs sm:text-sm sm:leading-6"
                                    />

                                    {shiftFormValidation.touched.validTo && shiftFormValidation.errors.validTo && (
                                        <p className="text-error text-xs mt-1">{shiftFormValidation.errors.validTo}</p>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <div className="mt-6 flex items-center justify-end gap-x-3">
                    <button
                        onClick={handleBack}
                        type="button"
                        className="inline-flex items-center justify-center rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50"
                    >
                        {t('Cancel')}
                    </button>
                    <button
                        disabled={submitLoading}
                        onClick={() => {
                            console.log('Form Data:', shiftFormValidation)
                            shiftFormValidation.handleSubmit()
                        }}
                        type="button"
                        className="rounded-md bg-primary px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-primary/[0.9] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary"
                    >
                        {t('Save')}
                    </button>
                </div>
            </form>
            {showErrDialog && (
                <ErrorDialog status={true} title="Error" message={addError} onClose={handleDialogClose} />
            )}
            {showSucDialog && (
                <SuccessDialog
                    status={true}
                    title={t('Success')}
                    message={t('Captain Shift Saved successfully')}
                    onClose={handleDialogClose}
                />
            )}
            {/* Overlay and Loading Spinner */}
            {submitLoading && (
                <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center">
                    <div className="animate-spin rounded-full h-16 w-16 border-t-4 border-b-4 border-white"></div>
                </div>
            )}
        </>
    )
}
